import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { SolMaterialCheckerMaterialChangesDialogComponent } from '../sol-material-checker-material-changes-dialog/sol-material-checker-material-changes-dialog.component';
import { MatRadioModule } from '@angular/material/radio';
import { MatCheckbox } from '@angular/material/checkbox';
import { MaterialCheckerService } from '../../../../services/api/material-checker.service';
import { CheckUpdatedMaterialRequest, CheckUpdatedMaterialResponse, BusinessTypes} from '../../../../models/dtos';
import { IndexDBService } from '../../../../services/common/indexDB.service';
import { CommonDataService } from '../../../../services/common-data.service';
import { SolCalculationsService } from '../../../../services/operations/sol-calculations.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-material-checker-dialog',
  imports: [
    MatButtonModule,
    TranslatePipe,
    CommonModule,
    FormsModule,
    FormsModule,
    MatRadioModule,
    MatCheckbox
  ],
  templateUrl: './sol-material-checker-dialog.component.html',
  styleUrl: './sol-material-checker-dialog.component.scss'
})
export class SolMaterialCheckerDialogComponent implements OnInit, OnDestroy {

  @Output() closeDialogEvent = new EventEmitter<void>();
  private readonly destroy$ = new Subject<void>();
  isReadOnlyMode = false;

  checkCompleted: boolean = false;
  updateChangesCompleted: boolean = false;
  hasChanges: boolean = false;
  selectedCheckType: string = 'fullpartcheck';
  standardLibrary: boolean = true;
  localLibrary: boolean = true;
  siParts: boolean = true;
  materialCheckerResponse: CheckUpdatedMaterialResponse;

  constructor(
    private readonly dialog: MatDialog,
    private readonly materialCheckerService: MaterialCheckerService,
    private readonly indexDBService: IndexDBService,
    private readonly commonDataService: CommonDataService,
    private readonly solCalculationsService: SolCalculationsService
  ){}

  ngOnInit(): void {
    this.setupReadOnlyMode();
  }
  private setupReadOnlyMode(): void {
    this.commonDataService.readOnlyMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isReadOnly => {
        this.isReadOnlyMode = isReadOnly;
      });
  
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    this.closeDialogEvent.emit();
  }

  openChangesList(){
    const dialogRef = this.dialog.open(SolMaterialCheckerMaterialChangesDialogComponent, {
      width: '900px',
      height: '400px',
      disableClose: true,
      data: { itemChanges: this.materialCheckerResponse?.itemChanges ?? [] }
    });
  }

  async goNext(){
    if (this.isReadOnlyMode) {
      return;
    }

    const materialLevelwiseData = await this.indexDBService.getAllMaterialData();

    const request = new CheckUpdatedMaterialRequest();
    request.materialLevelwiseData = materialLevelwiseData;
    request.cultureId = this.commonDataService.getEstimateCultureId();
    request.isIgnoreTBLFlag = false;
    request.isFullPartCheck = this.selectedCheckType === 'fullpartcheck';
    request.isStandardLibraryChecked = this.standardLibrary;
    request.isLocalLibraryChecked = this.localLibrary;
    request.isePartsChecked = this.siParts;
    request.business = this.commonDataService.getBusiness().charCodeAt(0) as BusinessTypes;

    this.materialCheckerService.getMaterialCheckerResult(request).subscribe({
      next: (response) => {
        this.materialCheckerResponse = response;
        this.hasChanges = !!response?.itemChanges?.length;
        this.checkCompleted = true;
      }
    });
  }

  async updateChanges(){
    if (this.isReadOnlyMode) {
      return;
    }

    const updatedMaterialData = this.materialCheckerResponse.materialLevelWiseDataWithAppliedChanges;
    try {
      await this.indexDBService.storeMaterialData(updatedMaterialData);
    } catch (error) {
      console.error('Failed to store updated material data.', error);
      return;
    }

    const [laborData, miscData] = await Promise.all([
      this.indexDBService.getAllLaborData(),
      this.indexDBService.getAllMiscData()
    ]);

    this.solCalculationsService.setLevelWiseCollections(laborData, miscData, updatedMaterialData);
    this.solCalculationsService.calculateCostsAndSellPricesWithoutCollections();

    this.updateChangesCompleted = true;
  }


}
